import { UserCircleIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router";
import React, { useEffect } from "react";
import { graphql } from "~/graphql/codegen";
import { useMutation } from "@apollo/client/react";
import type {
  SpeakerFragment,
  FollowProfileMutation,
  FollowProfileMutationVariables,
  UnfollowProfileMutation,
  UnfollowProfileMutationVariables,
} from "~/graphql/codegen/graphql";
import useAuthCheck from "~/hooks/check-authenticated";
import { classNames } from "~/utils";
import { Avatar, AvatarImage, toast } from "@mindstonehq/ui";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

const FOLLOW_PROFILE_MUTATION = graphql(`
  mutation followProfile($userID: ID!) {
    followProfile(userID: $userID)
  }
`);

const UNFOLLOW_PROFILE_MUTATION = graphql(`
  mutation unfollowProfile($userID: ID!) {
    unfollowProfile(userID: $userID)
  }
`);

export default function MindspaceEventSpeakers({ speakers: initialSpeakers }: { speakers: SpeakerFragment[] }) {
  const { t } = useLingui();
  const checkAuth = useAuthCheck();
  const [followProfile] = useMutation<FollowProfileMutation, FollowProfileMutationVariables>(FOLLOW_PROFILE_MUTATION);
  const [unfollowProfile] = useMutation<UnfollowProfileMutation, UnfollowProfileMutationVariables>(
    UNFOLLOW_PROFILE_MUTATION,
  );
  const [speakers, setSpeakers] = React.useState<SpeakerFragment[]>([]);

  useEffect(() => {
    setSpeakers(initialSpeakers);
  }, [initialSpeakers]);

  const handleFollow = (speaker: SpeakerFragment) => {
    const authenticated = checkAuth(t(msg`You need to be logged in to follow users`), `${location.href}`, "follow");

    if (!authenticated) {
      return;
    }

    if (speaker.following) {
      unfollowProfile({
        variables: {
          userID: speaker.id,
        },
      })
        .then(() => {
          setSpeakers((prev) =>
            prev.map((prevSpeaker) => {
              if (prevSpeaker.id === speaker.id) {
                return {
                  ...prevSpeaker,
                  following: false,
                };
              }
              return prevSpeaker;
            }),
          );
        })
        .catch((error: Error) => {
          toast.error(t(msg`Error unfollowing user`), {
            description: t(msg`Please try again later.`),
          });
          console.error(error);
        });
    } else {
      followProfile({
        variables: {
          userID: speaker.id,
        },
      })
        .then(() => {
          setSpeakers((prev) =>
            prev.map((prevSpeaker) => {
              if (prevSpeaker.id === speaker.id) {
                return {
                  ...prevSpeaker,
                  following: true,
                };
              }
              return prevSpeaker;
            }),
          );
        })
        .catch((error: Error) => {
          toast.error(t(msg`Error following user`), {
            description: t(msg`Please try again later.`),
          });
          console.error(error);
        });
    }
  };
  return (
    <div className="flex flex-col space-y-2">
      <h6 className="flex items-center">
        <UserCircleIcon className="h-5 w-5 mr-1" />
        <span><Trans>Speakers</Trans></span>
      </h6>
      <div className="flex flex-col sm:flex-row space-x-4 items-center">
        {speakers.map((speaker) => (
          <Link
            to={`/profile/${speaker.username}`}
            key={speaker.id}
            className="flex flex-col lg:bg-muted items-center justify-between p-5 rounded-xl space-y-5 flex-1 max-w-[280px]"
          >
            <div className="flex flex-col items-center">
              <Avatar className="h-12 w-12">
                <AvatarImage src={speaker.profilePictureURL} />
              </Avatar>
              <div className="flex flex-col items-center text-center">
                <div className="t-body-lg-bold">{speaker.displayName}</div>
                <div className="t-body-sm-regular">{speaker.tagLine}</div>
              </div>
            </div>
            {!speaker.mine && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleFollow(speaker);
                }}
                className={classNames(
                  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-9 px-4 py-2",
                  speaker.following
                    ? "border border-border bg-background hover:bg-accent hover:text-accent-foreground"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                )}
              >
                {speaker.following ? t(msg`Following`) : t(msg`Follow`)}
              </button>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
